/* Reusable points breakdown card. Used on the participant Dashboard and on the
 * facilitator's ParticipantDetail view so both sides see the same scoreboard.
 *
 * v2 (Feb 2026): now also shows EARNED vs POSSIBLE per row when total_tracks
 * is known, and a "Why isn't this 100%?" section that itemizes the gap.
 * Backend authority: POINTS dict in server.py — keep these labels and the
 * per-track-max math in sync if those constants change.
 */

const KEY_LABELS = {
  readings_complete: "Readings completed (Mon)",
  journal_entries: "Journal reflections written",
  ai_feedback_used: "AI mentor feedback used",
  assessments_submitted: "Assessments submitted",
  assessment_score_bonus: "Assessment score bonus",
  friday_complete: "Friday round-tables (peer engagement)",
  tracks_fully_complete: "Tracks fully complete",
  forum_posts: "Forum threads started",
  forum_replies: "Forum replies",
};

// Per-track expected maximum for each category. Used to surface the gap on
// the Dashboard. Forum activity is intentionally absent — it's open-ended
// and not counted toward the 120-per-track soft cap.
const PER_TRACK_MAX = {
  readings_complete: 10,
  journal_entries: 25,          // 5 quality journals/track × 5 pts
  ai_feedback_used: 10,         // 5 journals/track × 2 pts
  assessments_submitted: 20,
  assessment_score_bonus: 20,   // full 20 only at 100% score
  friday_complete: 10,
  tracks_fully_complete: 25,
};

const GAP_HINTS = {
  readings_complete: "Mark each Monday session complete to capture this.",
  journal_entries: "Write a substantive (60+ word, on-topic) reflection on each track's Monday prompt.",
  ai_feedback_used: "Tap 'Get AI feedback' on each journal entry to earn the mentor bonus.",
  assessments_submitted: "Submit the Wednesday 4-part assessment on every track.",
  assessment_score_bonus: "Aim for 100% on the multiple-choice + true/false to earn the full score bonus.",
  friday_complete: "Mark each Friday round-table session complete.",
  tracks_fully_complete: "Complete all three sessions (Mon, Wed, Fri) of a track to unlock the +25 bonus.",
};

export default function PointsBreakdownCard({
  breakdown,
  totalTracks,
  totalPoints,
  className = "",
  title = "How you earned your points",
}) {
  if (!breakdown) return null;

  // If we know how many tracks the user is enrolled in, we can compute the
  // soft cap (the same 120-per-track formula the backend uses for percent),
  // and per-row "earned of possible" with a gap. Otherwise we degrade
  // gracefully to the legacy display.
  const showGapDetail = typeof totalTracks === "number" && totalTracks > 0;
  const softMax = showGapDetail ? totalTracks * 120 : null;

  // Build per-row gap items
  const gaps = [];
  if (showGapDetail) {
    for (const [k, max_per_track] of Object.entries(PER_TRACK_MAX)) {
      const earned = breakdown[k]?.points ?? 0;
      const possible = max_per_track * totalTracks;
      const missing = Math.max(0, possible - earned);
      if (missing > 0) {
        gaps.push({ key: k, earned, possible, missing, hint: GAP_HINTS[k] });
      }
    }
    gaps.sort((a, b) => b.missing - a.missing);
  }

  return (
    <div className={`card-bento ${className}`} data-testid="points-breakdown">
      <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
        <div className="overline">{title}</div>
        {showGapDetail && (
          <div className="text-xs text-[#8A8782]" data-testid="points-vs-max">
            <span className="text-[#C9A961] tabular-nums">{totalPoints ?? "—"}</span>
            <span> / {softMax} possible (forum activity counts on top)</span>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {Object.entries(breakdown).map(([k, v]) => {
          const possible = PER_TRACK_MAX[k] && showGapDetail
            ? PER_TRACK_MAX[k] * totalTracks
            : null;
          return (
            <div
              key={k}
              className="flex items-center justify-between py-2 border-b border-[#C9A961]/10 last:border-b-0"
            >
              <div>
                <div className="text-sm text-[#F7F5F0]">{KEY_LABELS[k] || k}</div>
                <div className="text-xs text-[#8A8782]">{v.count} × earned</div>
              </div>
              <div className="text-[#C9A961] tabular-nums text-right">
                +{v.points}
                {possible !== null && possible > v.points && (
                  <span className="text-[#8A8782] text-xs"> / {possible}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showGapDetail && gaps.length > 0 && (
        <details className="mt-6 border-t border-[#C9A961]/15 pt-4" data-testid="points-gap-details">
          <summary className="cursor-pointer text-sm text-[#C9A961] hover:text-[#E2C97A]">
            Why isn't this 100%?
          </summary>
          <div className="mt-3 space-y-2 text-xs text-[#D1CFC9]">
            {gaps.map((g) => (
              <div key={g.key} className="flex items-start gap-2" data-testid={`gap-${g.key}`}>
                <span className="text-[#8A8782] tabular-nums mt-0.5">-{g.missing}</span>
                <div>
                  <div className="text-[#F7F5F0]">{KEY_LABELS[g.key]}</div>
                  <div className="text-[#8A8782]">{g.hint}</div>
                </div>
              </div>
            ))}
            <p className="text-[10px] text-[#8A8782] italic pt-2">
              Forum posts and replies aren't capped — they add on top of the 120-per-track ceiling, so it's possible to finish the program with more than the "possible" total.
            </p>
          </div>
        </details>
      )}
    </div>
  );
}

export { KEY_LABELS };
